import { useState } from 'react';
import { DataTable } from '../../../components/ui/DataTable';
import { Button } from '../../../components/ui/Button';
import { DropdownMenu } from '../../../components/ui/DropdownMenu';
import { Download, Eye, FileText, AlertTriangle, MoreVertical, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { DECLARATION_TYPES, DECLARATION_STATUS_LABELS, PERIODICITY_LABELS } from '../../../types/declarations';
import type { Declaration } from '../../../types/declarations';
import { useCurrency } from '../../../hooks/useCurrency';
import { useFiscalYear } from '../../../hooks/useFiscalYear';
import type { DeclarationFiltersState } from './DeclarationFilters';

interface DeclarationListProps {
  declarations: Declaration[];
  filters: DeclarationFiltersState;
  onPreview: (declaration: Declaration) => void;
  onDownload: (declaration: Declaration) => void;
  onEdit?: (declaration: Declaration) => void;
  onDelete?: (declaration: Declaration) => void;
  onValidate?: (declaration: Declaration) => void;
  onReject?: (declaration: Declaration) => void;
}

export function DeclarationList({ 
  declarations, 
  filters,
  onPreview, 
  onDownload,
  onEdit,
  onDelete,
  onValidate,
  onReject
}: DeclarationListProps) {
  const { format, currentCurrency, convertAmount } = useCurrency();
  const { currentFiscalYear } = useFiscalYear();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Protection contre les données undefined
  const safeDeclarations = Array.isArray(declarations) ? declarations : [];

  // Filtrer les déclarations par année fiscale courante
  const fiscalYearDeclarations = safeDeclarations.filter(declaration => {
    if (!currentFiscalYear) {
      return true; // Afficher toutes les déclarations si pas d'année fiscale définie
    }
    
    const declarationDate = new Date(declaration.period);
    const fiscalYearStart = new Date(currentFiscalYear.startDate);
    const fiscalYearEnd = new Date(currentFiscalYear.endDate);
    
    return declarationDate >= fiscalYearStart && declarationDate <= fiscalYearEnd;
  });

  // Appliquer les filtres utilisateur
  const filteredDeclarations = fiscalYearDeclarations.filter((declaration: Declaration) => {
    // Filtre par type
    if (filters.type !== 'all') {
      if (filters.type.startsWith('category:')) {
        const categoryFilter = filters.type.replace('category:', '');
        const declarationType = DECLARATION_TYPES[declaration.type];
        if (declarationType.category !== categoryFilter) return false;
      } else if (declaration.type !== filters.type) {
        return false;
      }
    }

    // Filtre par catégorie  
    if (filters.category !== 'all') {
      const declarationType = DECLARATION_TYPES[declaration.type];
      if (declarationType.category !== filters.category) return false;
    }

    // Filtre par statut
    if (filters.status !== 'all' && declaration.status !== filters.status) {
      return false;
    }

    // Filtre par période
    if (filters.period !== 'all') {
      const declarationDate = new Date(declaration.period);
      const now = new Date();
      
      switch (filters.period) {
        case 'current_month':
          if (declarationDate.getMonth() !== now.getMonth() || 
              declarationDate.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'current_quarter':
          const currentQuarter = Math.floor(now.getMonth() / 3);
          const declQuarter = Math.floor(declarationDate.getMonth() / 3);
          if (declQuarter !== currentQuarter || 
              declarationDate.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'current_year':
          if (declarationDate.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'last_month':
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
          if (declarationDate.getMonth() !== lastMonth.getMonth() || 
              declarationDate.getFullYear() !== lastMonth.getFullYear()) return false;
          break;
        case 'last_quarter':
          const currentQuarterForLast = Math.floor(now.getMonth() / 3);
          const declQuarterForLast = Math.floor(declarationDate.getMonth() / 3);
          const lastQuarter = currentQuarterForLast === 0 ? 3 : currentQuarterForLast - 1;
          const lastQuarterYear = currentQuarterForLast === 0 ? now.getFullYear() - 1 : now.getFullYear();
          if (declQuarterForLast !== lastQuarter || 
              declarationDate.getFullYear() !== lastQuarterYear) return false;
          break;
        case 'last_year':
          if (declarationDate.getFullYear() !== now.getFullYear() - 1) return false;
          break;
      }
    }

    return true;
  });

  // Vérifier si l'IBP a été payé pour permettre l'audit
  const ibpPaid = filteredDeclarations.some(
    declaration => declaration.type === 'IBP' && declaration.status === 'validated'
  );

  // Pagination
  const totalPages = Math.ceil(filteredDeclarations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  // Paginated declarations pour l'affichage
  const displayDeclarations = filteredDeclarations.slice(startIndex, startIndex + itemsPerPage);

  const formatPeriod = (declaration: Declaration) => {
    const declarationType = DECLARATION_TYPES[declaration.type];
    const date = new Date(declaration.period);
    
    if (declarationType.periodicity === 'annual') {
      return date.getFullYear().toString();
    } else if (declarationType.periodicity === 'quarterly') {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    } else {
      return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  };

  const getStatusColor = (status: Declaration['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'submitted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'validated': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const canAudit = (declaration: Declaration) => {
    // On ne peut auditer que si l'IBP a été payé et validé
    return ibpPaid || declaration.type === 'IBP';
  };

  const columns = [
    {
      header: 'Type & Catégorie',
      accessor: (declaration: Declaration) => {
        const declarationType = DECLARATION_TYPES[declaration.type];
        const categoryLabel = {
          direct_tax: 'Impôt Direct',
          indirect_tax: 'Taxe Indirecte', 
          social_contribution: 'Cotisation Sociale',
          special_tax: 'Taxe Spécifique',
          other: 'Autres'
        }[declarationType.category];

        return (
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {declarationType.label}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {categoryLabel} • {PERIODICITY_LABELS[declarationType.periodicity]} • {declarationType.rate}
            </div>
          </div>
        );
      }
    },
    {
      header: 'Période',
      accessor: (declaration: Declaration) => (
        <div className="text-gray-900 dark:text-gray-100">
          {formatPeriod(declaration)}
        </div>
      )
    },
    {
      header: 'Date limite',
      accessor: (declaration: Declaration) => {
        const dueDate = new Date(declaration.dueDate);
        const overdue = isOverdue(declaration.dueDate);
        return (
          <div className={`flex items-center gap-2 ${
            overdue 
              ? 'text-red-600 dark:text-red-400' 
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            {overdue && <AlertTriangle className="h-4 w-4" />}
            {dueDate.toLocaleDateString('fr-FR')}
          </div>
        );
      }
    },
    {
      header: `Montants (${currentCurrency})`,
      accessor: (declaration: Declaration) => {
        const baseAmount = convertAmount(declaration.amount, 'CDF', currentCurrency);
        const additionalFees = declaration.additionalFees ? convertAmount(declaration.additionalFees, 'CDF', currentCurrency) : 0;
        const penalties = declaration.penalties ? convertAmount(declaration.penalties, 'CDF', currentCurrency) : 0;
        const total = baseAmount + additionalFees + penalties;

        return (
          <div className="text-right">
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {format(baseAmount, currentCurrency)}
            </div>
            {(additionalFees > 0 || penalties > 0) && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {additionalFees > 0 ? `+${format(additionalFees, currentCurrency)} frais` : ''}
                {additionalFees > 0 && penalties > 0 ? ' • ' : ''}
                {penalties > 0 ? `+${format(penalties, currentCurrency)} pénalités` : ''}
              </div>
            )}
            {(additionalFees > 0 || penalties > 0) && (
              <div className="text-sm font-medium border-t border-gray-200 dark:border-gray-700 pt-1 mt-1 text-gray-900 dark:text-gray-100">
                Total: {format(total, currentCurrency)}
              </div>
            )}
          </div>
        );
      },
      className: 'text-right'
    },
    {
      header: 'Statut',
      accessor: (declaration: Declaration) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(declaration.status)}`}>
          {DECLARATION_STATUS_LABELS[declaration.status]}
        </span>
      )
    },
    {
      header: 'Documents',
      accessor: (declaration: Declaration) => (
        <div className="flex items-center gap-2">
          {declaration.justificationDocument && (
            <div title="Justificatif disponible">
              <FileText className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
          )}
          {declaration.declarationForm && (
            <div title="Formulaire disponible">
              <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
          )}
          {!declaration.justificationDocument && (
            <div title="Justificatif manquant">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: (declaration: Declaration) => {
        const auditAllowed = canAudit(declaration);
        const menuItems = [
          {
            label: 'Voir les détails',
            icon: Eye,
            onClick: () => onPreview(declaration)
          },
          {
            label: 'Télécharger',
            icon: Download,
            onClick: () => onDownload(declaration),
            disabled: declaration.status === 'draft'
          },
          ...(onEdit ? [{
            label: 'Modifier',
            icon: Edit,
            onClick: () => onEdit(declaration),
            disabled: declaration.status === 'validated'
          }] : []),
          ...(onValidate && declaration.status === 'submitted' ? [{
            label: 'Valider',
            icon: CheckCircle,
            onClick: () => onValidate(declaration),
            disabled: !auditAllowed,
            tooltip: !auditAllowed ? 'L\'IBP doit être payé avant de valider d\'autres déclarations' : undefined
          }] : []),
          ...(onReject && declaration.status === 'submitted' ? [{
            label: 'Rejeter',
            icon: XCircle,
            onClick: () => onReject(declaration)
          }] : []),
          ...(onDelete ? [{
            label: 'Supprimer',
            icon: Trash2,
            onClick: () => onDelete(declaration),
            disabled: declaration.status === 'validated',
            variant: 'danger' as const
          }] : [])
        ];

        return (
          <DropdownMenu
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={MoreVertical}
                className="h-8 w-8 p-0"
              />
            }
            items={menuItems}
          />
        );
      }
    }
  ];

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={displayDeclarations}
        emptyMessage="Aucune déclaration trouvée"
        showToolbar={true}
        enableResize={true}
        enableSort={true}
        enableFilters={true}
        enableColumnToggle={true}
        enableGlobalFilter={true}
        showPagination={true}
        page={currentPage}
        totalPages={totalPages}
        totalItems={filteredDeclarations.length}
        onPageChange={setCurrentPage}
        storageKey="declarations-list-table"
        density="normal"
        maxHeight="calc(100vh - 400px)"
      />
    </div>
  );
}